import React from 'react';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const ReportingCapabilities = () => {
  const reportTypes = [
  {
    icon: "BarChart3",
    title: "Daily Sales Analytics",
    description: "Comprehensive daily reports showing revenue, transactions, top products, and hourly sales patterns with visual charts."
  },
  {
    icon: "CreditCard",
    title: "Payment Integration Reports",
    description: "Detailed breakdowns of payment methods, transaction fees, settlement times, and payment gateway performance."
  },
  {
    icon: "Users",
    title: "Customer Loyalty Insights",
    description: "Track customer purchase history, loyalty points, redemption rates, and identify your most valuable customers."
  },
  {
    icon: "TrendingUp",
    title: "Profit Margin Analysis",
    description: "Calculate profit margins by product, category, or store with cost tracking and markup percentage insights."
  }];


  return (
    <section className="py-16 md:py-20 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1 relative">

            <div className="relative rounded-2xl overflow-hidden shadow-purple-lg">
              <Image
                src="/cloud-app-screenshot/analytics-light.png"
                alt="Cultaz analytics dashboard displaying sales reports, charts, and data visualizations"
                className="w-full h-64 md:h-80 lg:h-96 object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
            </div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="absolute -top-4 -left-4 md:-top-6 md:-left-6 bg-card rounded-xl shadow-purple-md p-4 md:p-6 border border-border">

              <div className="flex items-center space-x-3 md:space-x-4">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="FileText" size={24} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div>
                  <p className="font-body text-xs md:text-sm text-muted-foreground">Reports Generated</p>
                  <p className="font-heading text-xl md:text-2xl font-bold text-foreground">50K+</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2">

            <div className="inline-flex items-center px-4 py-2 rounded-full bg-secondary/10 mb-4 md:mb-6">
              <Icon name="BarChart3" size={18} color="var(--color-secondary)" strokeWidth={2} />
              <span className="ml-2 font-body text-sm font-medium text-secondary">Reporting & Analytics</span>
            </div>
            
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 md:mb-6">
              Comprehensive Business Intelligence
            </h2>
            
            <p className="font-body text-base md:text-lg text-muted-foreground mb-6 md:mb-8">
              Make data-driven decisions with powerful reporting tools that provide deep insights into your sales, inventory, and customer behavior.
            </p>
            
            <div className="space-y-4 md:space-y-6">
              {reportTypes?.map((report, index) =>
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start space-x-4">

                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
                    <Icon name={report?.icon} size={20} color="var(--color-secondary)" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground mb-1 md:mb-2">
                      {report?.title}
                    </h3>
                    <p className="font-body text-sm md:text-base text-muted-foreground">
                      {report?.description}
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>);


};

export default ReportingCapabilities;